// packages/app/src/scaffolder/TeamLeadGuard.tsx

import { useEffect, useState } from 'react';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { useNavigate } from 'react-router-dom';
import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';

import { parseEksTeam } from '../utils/eksTeam';

/**
 * TeamLeadGuardComponent
 *
 * Verifies the caller is a lead of at least one team (or a platform lead).
 * Passes the caller's ownershipEntityRefs as JSON to the backend action
 * so assertTeamLead can verify team ownership in environments without
 * catalog user entities (such as toolsdev).
 */
export const TeamLeadGuardComponent = ({
  onChange,
}: FieldExtensionComponentProps<string>) => {
  const identityApi = useApi(identityApiRef);
  const navigate    = useNavigate();
  const [checking, setChecking] = useState(true);
  const [denied, setDenied]     = useState(false);

  useEffect(() => {
    identityApi.getBackstageIdentity().then(identity => {
      const refs = identity.ownershipEntityRefs ?? [];
      const isAnyLead = refs.some(ref => {
        const parsed = parseEksTeam(ref);
        return parsed !== null && (parsed.role === 'lead' || (parsed.isPlatform && parsed.role === 'dev'));
      });

      if (!isAnyLead) {
        setDenied(true);
        setTimeout(() => navigate('/create'), 1500);
      } else {
        onChange(JSON.stringify(refs));
      }
      setChecking(false);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (checking) {
    return (
      <Box display="flex" alignItems="center" style={{ gap: 8 }}>
        <CircularProgress size={16} />
        <Typography variant="caption">Checking authorization…</Typography>
      </Box>
    );
  }

  if (denied) {
    return (
      <Box>
        <Typography variant="body2" color="error">
          You do not have permission to create this resource. Redirecting…
        </Typography>
      </Box>
    );
  }

  return null;
};
